/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {AdnShotterService} from '../../common/adn-shotter.service';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './preview-shotter-page.component.html';
import {SIMPLE_PAGE_IMPORTS} from './simple-page-imports';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

@Component({
  standalone: true,
  imports: SIMPLE_PAGE_IMPORTS,
  selector: 'adn-preview-shotter-page',
  template
})
export class PreviewShotterPageComponent implements OnInit {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'CUSTOM';

  width = 300;
  height = 250;
  device = 'DESKTOP';
  private readonly asyncState = signal<any>({
    screenshots: undefined,
    screenshotting: false,
    deleting: false,
    loaded: true,
    takingScreenshot: false,
    takeSuccess: false,
    takeError: '',
    resp: null,
    missingPreviewShots: undefined,
    fetchingMissingPreview: false,
    triggeringMissingPreview: undefined,
    previewsByCreativePage: undefined,
    fetchingPreviewsByCreative: false
  });
  private readAsync(key: string): any { return this.asyncState()[key]; }
  private writeAsync(key: string, value: any): void {
    const state = this.asyncState();
    if (!Object.is(state[key], value)) {
      this.asyncState.set({...state, [key]: value});
    }
  }
  get screenshots(): any[] { return this.readAsync('screenshots'); }
  set screenshots(value: any[]) { this.writeAsync('screenshots', value); }
  get screenshotting(): boolean { return this.readAsync('screenshotting'); }
  set screenshotting(value: boolean) { this.writeAsync('screenshotting', value); }
  get deleting(): boolean { return this.readAsync('deleting'); }
  set deleting(value: boolean) { this.writeAsync('deleting', value); }
  showScreenshots = false;
  get loaded(): boolean { return this.readAsync('loaded'); }
  set loaded(value: boolean) { this.writeAsync('loaded', value); }
  get takingScreenshot(): boolean { return this.readAsync('takingScreenshot'); }
  set takingScreenshot(value: boolean) { this.writeAsync('takingScreenshot', value); }
  get takeSuccess(): boolean { return this.readAsync('takeSuccess'); }
  set takeSuccess(value: boolean) { this.writeAsync('takeSuccess', value); }
  get takeError(): string { return this.readAsync('takeError'); }
  set takeError(value: string) { this.writeAsync('takeError', value); }
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  hashResults = true;
  get resp(): any { return this.readAsync('resp'); }
  set resp(value: any) { this.writeAsync('resp', value); }

  get missingPreviewShots(): any[] { return this.readAsync('missingPreviewShots'); }
  set missingPreviewShots(value: any[]) { this.writeAsync('missingPreviewShots', value); }
  get fetchingMissingPreview(): boolean { return this.readAsync('fetchingMissingPreview'); }
  set fetchingMissingPreview(value: boolean) { this.writeAsync('fetchingMissingPreview', value); }
  showMissingPreview = false;
  get triggeringMissingPreview(): string { return this.readAsync('triggeringMissingPreview'); }
  set triggeringMissingPreview(value: string) { this.writeAsync('triggeringMissingPreview', value); }

  previewsByCreativeMeta = {
    currentPage: 1,
    pageSize: 20,
    resolved: true
  };
  get previewsByCreativePage(): any { return this.readAsync('previewsByCreativePage'); }
  set previewsByCreativePage(value: any) { this.writeAsync('previewsByCreativePage', value); }
  get fetchingPreviewsByCreative(): boolean { return this.readAsync('fetchingPreviewsByCreative'); }
  set fetchingPreviewsByCreative(value: boolean) { this.writeAsync('fetchingPreviewsByCreative', value); }
  showPreviewsByCreative = false;

  constructor(
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnShotter: AdnShotterService,
    private readonly uiRouter: UIRouter,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  ngOnInit(): void {
    const creativeId = this.uiRouter.globals.params.creativeId;
    if (creativeId) {
      this.objectId = creativeId;
      this.doCheck();
    }
  }

  refresh(): void {
    this.screenshotting = true;
    this.showScreenshots = true;
    this.adnShotter.fetchAllPreviews().then((data: any[]) => {
      this.screenshots = data;
    }).finally(() => {
      this.screenshotting = false;
    });
  }

  refreshMissingPreview(): void {
    this.fetchingMissingPreview = true;
    this.showMissingPreview = true;
    this.adnShotter.missingPreview().then((data: any[]) => {
      this.missingPreviewShots = data;
    }).finally(() => {
      this.fetchingMissingPreview = false;
    });
  }

  refreshPreviewsByCreative(): void {
    this.fetchPreviewsByCreative(this.previewsByCreativeMeta.currentPage);
  }

  changePreviewsByCreativePage(page: number): void {
    if (page < 1 || page > this.previewsByCreativeTotalPages() || page === this.previewsByCreativeMeta.currentPage) {
      return;
    }
    this.previewsByCreativeMeta.currentPage = page;
    this.fetchPreviewsByCreative(page);
  }

  previewsByCreativeTotalPages(): number {
    return Math.max(1, Math.ceil((this.previewsByCreativePage?.totalHits || 0) / this.previewsByCreativeMeta.pageSize));
  }

  previewsByCreativePageNumbers(): number[] {
    return visiblePageNumbers(this.previewsByCreativeMeta.currentPage, this.previewsByCreativeTotalPages());
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting = true;
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      _.pullAt(this.screenshots, index);
    }).finally(() => {
      this.deleting = false;
    });
  }

  doCheck(): void {
    if (!this.objectId) {
      this.takeSuccess = false;
      this.takeError = 'Enter a creative ID first.';
      return;
    }
    this.loaded = false;
    this.takingScreenshot = true;
    this.takeSuccess = false;
    this.takeError = '';
    this.resp = null;
    this.adnShotter.takePreview(this.objectId, this.device).then((data: any) => {
      this.resp = data;
      this.takeSuccess = true;
    }).catch((error: any) => {
      this.takeError = _.get(error, ['data', 'message'])
        || _.get(error, ['data'])
        || 'Unable to queue the preview screenshot.';
      if (!_.isString(this.takeError)) {
        this.takeError = 'Unable to queue the preview screenshot.';
      }
    }).finally(() => {
      this.loaded = true;
      this.takingScreenshot = false;
    });
  }

  takeMissingPreview(creativeId: string): void {
    this.triggeringMissingPreview = creativeId;
    this.adnShotter.takePreview(creativeId, this.device).finally(() => {
      this.triggeringMissingPreview = null;
      this.refreshMissingPreview();
    });
  }

  private fetchPreviewsByCreative(page?: number): void {
    this.fetchingPreviewsByCreative = true;
    this.showPreviewsByCreative = true;
    this.adnShotter.previewsByCreative(page || this.previewsByCreativeMeta.currentPage).then((data: any) => {
      this.previewsByCreativeMeta.currentPage = data.page;
      this.previewsByCreativeMeta.pageSize = data.pageSize;
      this.previewsByCreativePage = {
          totalHits: data.totalCount,
          pageStart: data.totalCount ? ((data.page - 1) * data.pageSize) + 1 : 0,
          pageEnd: Math.min(data.totalCount, data.page * data.pageSize),
          items: _.map(data.results, (screenshots: any[], creativeId: string) => ({
            creativeId: creativeId,
            creative: _.get(screenshots, [0, 'creative']),
            lineItem: _.get(screenshots, [0, 'lineItem']),
            screenshots: screenshots
          }))
      };
    }).finally(() => {
      this.fetchingPreviewsByCreative = false;
    });
  }
}
